import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useFinance } from "../contexts/FinanceContext";
import { isWithinInterval, subWeeks, subMonths } from "date-fns";
import { FaTrashAlt, FaEdit, FaCheck } from "react-icons/fa";
import { useTranslation } from "react-i18next";

export default function ExpenseLimit({ history }) {
  const { t } = useTranslation();
  const { expenseLimit, updateExpenseLimit, deleteExpenseLimit } = useFinance();
  const [limit, setLimit] = useState(expenseLimit?.limit || "");
  const [period, setPeriod] = useState(expenseLimit?.period || "month");
  const [editing, setEditing] = useState(!expenseLimit);

  // Calculate current period range
  const currentRange = useMemo(() => {
    const now = new Date();
    return period === "week"
      ? { start: subWeeks(now, 1), end: now }
      : { start: subMonths(now, 1), end: now };
  }, [period]);

  // Calculate spent within that range
  const spent = useMemo(() => {
    if (!history || !Array.isArray(history)) return 0;
    return history
      .filter(
        (h) =>
          h.type === "spending" &&
          isWithinInterval(new Date(h.date), {
            start: currentRange.start,
            end: currentRange.end,
          })
      )
      .reduce((sum, h) => sum + Number(h.amount || 0), 0);
  }, [history, currentRange]);

  const percent = limit ? Math.min((spent / limit) * 100, 100).toFixed(1) : 0;

  // ---------------- DYNAMIC COLORS ----------------
  let progressColor = "#22c55e"; // green default
  if (percent >= 70 && percent < 90) progressColor = "#facc15"; // yellow
  else if (percent >= 90) progressColor = "#ef4444"; // red

  // ---------------- DYNAMIC QUOTES ----------------
  let quote = "";
  if (percent < 30) quote = t("expenseLimit.greatStart");
  else if (percent < 60) quote = t("expenseLimit.balancedSpending");
  else if (percent < 80) quote = t("expenseLimit.spendingFast");
  else if (percent < 100) quote = t("expenseLimit.approachingLimit");
  else quote = t("expenseLimit.limitReachedMessage");

  // ---------------- HANDLERS ----------------
  const handleSave = async () => {
    if (!limit || limit <= 0) return alert(t("expenseLimit.enterValidLimit"));
    await updateExpenseLimit(limit, period);
    setEditing(false);
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm(t("expenseLimit.removeLimit"));
    if (confirmDelete) await deleteExpenseLimit();
  };

  // Function to handle variable replacements in translations
  const translateWithVariables = (key, variables = {}) => {
    let translation = t(key);
    Object.keys(variables).forEach((variable) => {
      translation = translation.replace(`{{${variable}}}`, variables[variable]);
    });
    return translation;
  };

  return (
    <LimitCard>
      <Header>
        <CenteredTitle>{t("expenseLimit.title")}</CenteredTitle>
        {!editing && (
          <EditIcon onClick={() => setEditing(true)}>
            <FaEdit />
          </EditIcon>
        )}
      </Header>

      {!expenseLimit || editing ? (
        <EditForm>
          <Label>{t("expenseLimit.setLimit")}</Label>
          <Input
            type="number"
            placeholder={t("expenseLimit.enterAmount")}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
          />

          <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="week">{t("expenseLimit.perWeek")}</option>
            <option value="month">{t("expenseLimit.perMonth")}</option>
          </Select>

          <ButtonRow>
            <SaveButton onClick={handleSave}>
              <FaCheck /> {t("expenseLimit.save")}
            </SaveButton>
            {expenseLimit && (
              <DeleteButton onClick={handleDelete}>
                <FaTrashAlt /> {t("expenseLimit.delete")}
              </DeleteButton>
            )}
          </ButtonRow>
        </EditForm>
      ) : (
        <Content>
          <ProgressWrapper>
            <ProgressBar
              style={{
                width: `${percent}%`,
                background: progressColor,
              }}
            />
          </ProgressWrapper>

          <SpentText>
            <strong>${spent.toFixed(2)}</strong>{" "}
            {translateWithVariables("expenseLimit.ofSpent", { limit })}
          </SpentText>

          <SubText>
            {percent < 100
              ? translateWithVariables("expenseLimit.remaining", {
                  percent: (100 - percent).toFixed(0),
                })
              : t("expenseLimit.limitReached")}
          </SubText>

          <Quote>{quote}</Quote>
        </Content>
      )}
    </LimitCard>
  );
}

// ---------------- STYLES ----------------

const LimitCard = styled.div`
  background: var(--secondaryBackgroundColor);
  border-radius: 16px;
  padding: 1.8em;
  color: var(--fontPrimaryColor);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  width: 100%;
  margin: auto;
  transition: 0.5s;
  height: stretch;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 14px;
`;

const CenteredTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  opacity: 0.9;
  text-align: center;
  width: 100%;
`;

const EditIcon = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  color: #9aa4c0;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    color: #38bdf8;
  }
`;

const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  opacity: 0.7;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: var(--thirdBackgroundColor);
  color: #fff;
  font-size: 15px;
  text-align: center;
  outline: none;
  transition: 0.2s;

  &:focus {
    background: #2a2e36;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: var(--thirdBackgroundColor);
  color: #fff;
  font-size: 15px;
  text-align: center;
  outline: none;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 6px;
`;

const SaveButton = styled.button`
  background: #38bdf8;
  border: none;
  color: white;
  padding: 10px 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    background: #0ea5e9;
  }
`;

const DeleteButton = styled.button`
  background: #ef4444;
  border: none;
  color: white;
  padding: 10px 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    background: #dc2626;
  }
`;

const Content = styled.div`
  text-align: center;
`;

const ProgressWrapper = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  height: 10px;
  overflow: hidden;
  margin: 12px 0 16px;
  margin-top: 2em;
`;

const ProgressBar = styled.div`
  height: 100%;
  border-radius: 10px;
  transition: width 0.4s ease, background 0.4s ease;
`;

const SpentText = styled.p`
  font-size: 16px;
  margin: 6px 0;
`;

const SubText = styled.p`
  font-size: 13px;
  opacity: 0.6;
`;

const Quote = styled.p`
  margin-top: 8px;
  font-size: 0.85rem;
  color: var(--fontPrimaryColor);
  font-weight: 600;
  transition: 0.3s;
`;
